import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError, coverUrl } from "../lib/api";
import { ListMusic, Plus, Play, Trash2, X } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";

const DEFAULT_COVER = "https://images.unsplash.com/photo-1659117675918-69ec794c64f2?crop=entropy&cs=srgb&fm=jpg&w=400&q=80";

export default function PlaylistsPage() {
  const [playlists, setPlaylists] = useState([]);
  const [open, setOpen] = useState(null); // open playlist id
  const [openDetail, setOpenDetail] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const { playQueue } = usePlayer();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/playlists");
      setPlaylists(data || []);
    } catch (e) { setErr(formatApiError(e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openPlaylist = async (id) => {
    setOpen(id);
    try {
      const { data } = await api.get(`/playlists/${id}`);
      setOpenDetail(data);
    } catch (e) { setErr(formatApiError(e)); }
  };

  const createPlaylist = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/playlists", { name, description: description || undefined });
      setName(""); setDescription(""); setShowCreate(false);
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const deletePlaylist = async (id) => {
    if (!window.confirm("Delete this playlist?")) return;
    try {
      await api.delete(`/playlists/${id}`);
      if (open === id) { setOpen(null); setOpenDetail(null); }
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  const removeTrack = async (trackId) => {
    try {
      await api.delete(`/playlists/${open}/tracks/${trackId}`);
      openPlaylist(open);
    } catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="overline text-[#C5A059] mb-2">Your collections</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#FDFBF7] tracking-tight">Playlists</h1>
        </div>
        <button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="create-playlist-btn">
          <Plus className="w-4 h-4" strokeWidth={1.5} /> New playlist
        </button>
      </div>

      {err && <div className="text-red-400 text-sm">{err}</div>}

      {showCreate && (
        <div className="card-surface p-6 space-y-3">
          <div className="flex items-center justify-between">
            <div className="overline text-[#C5A059]">Create playlist</div>
            <button onClick={() => setShowCreate(false)} className="text-[#A1A4AB] hover:text-[#FDFBF7]"><X className="w-5 h-5" strokeWidth={1.5} /></button>
          </div>
          <form onSubmit={createPlaylist} className="space-y-3" data-testid="create-playlist-form">
            <input required className="input-lux" placeholder="Playlist name" value={name} onChange={(e) => setName(e.target.value)} data-testid="playlist-name-input" />
            <textarea className="input-lux" placeholder="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="playlist-description-input" />
            <button type="submit" className="btn-primary" data-testid="playlist-create-submit">Create</button>
          </form>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="aspect-square rounded-lg shimmer" />)}</div>
      ) : playlists.length === 0 ? (
        <div className="card-surface p-16 text-center">
          <ListMusic className="w-8 h-8 text-[#C5A059] mx-auto mb-3" strokeWidth={1.5} />
          <div className="text-[#FDFBF7] font-serif text-2xl mb-2">No playlists yet</div>
          <div className="text-[#A1A4AB]">Create one and start curating your favorites.</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="playlists-grid">
          {playlists.map((p) => (
            <button
              key={p.id}
              onClick={() => openPlaylist(p.id)}
              className={`text-left group ${open === p.id ? "opacity-100" : ""}`}
              data-testid={`playlist-card-${p.id}`}
            >
              <div className="aspect-square rounded-lg overflow-hidden bg-gradient-to-br from-[#1A1D24] to-[#060709] border border-[#22262E] relative flex items-center justify-center">
                <img src={DEFAULT_COVER} alt="" className="w-full h-full object-cover opacity-50 hover-lift" />
                <ListMusic className="w-12 h-12 text-[#C5A059] absolute" strokeWidth={1} />
              </div>
              <div className="mt-3">
                <div className="text-[#FDFBF7] font-medium truncate">{p.name}</div>
                <div className="text-[#A1A4AB] text-sm">{p.track_ids?.length || 0} tracks</div>
              </div>
            </button>
          ))}
        </div>
      )}

      {openDetail && (
        <div className="card-surface p-6 space-y-4" data-testid="playlist-detail">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-serif text-2xl text-[#FDFBF7] tracking-tight">{openDetail.name}</h2>
              {openDetail.description && <p className="text-[#A1A4AB] text-sm mt-1">{openDetail.description}</p>}
              <div className="text-xs text-[#6C707A] mt-1">{openDetail.tracks?.length || 0} tracks</div>
            </div>
            <div className="flex gap-2">
              {openDetail.tracks?.length > 0 && (
                <button onClick={() => playQueue(openDetail.tracks, 0, "normal")} className="btn-primary" data-testid="playlist-play-all">
                  <Play className="w-4 h-4" strokeWidth={1.5} /> Play all
                </button>
              )}
              <button onClick={() => deletePlaylist(openDetail.id)} className="btn-secondary text-red-300 hover:text-red-400 border-red-400/30 hover:border-red-400/60" data-testid="playlist-delete-btn">
                <Trash2 className="w-4 h-4" strokeWidth={1.5} /> Delete
              </button>
            </div>
          </div>
          {openDetail.tracks?.length === 0 ? (
            <div className="text-[#A1A4AB] text-sm">Empty playlist. Add tracks from Music or Favorites pages by opening the track menu.</div>
          ) : (
            <div className="divide-y divide-[#22262E]/60 -mx-2">
              {openDetail.tracks.map((t, i) => (
                <div key={t.id} className="flex items-center gap-3 p-2 hover:bg-white/[0.02] rounded-md">
                  <div className="w-12 h-12 rounded-md overflow-hidden border border-[#22262E] bg-[#111317] flex-shrink-0">
                    <img src={coverUrl(t) || DEFAULT_COVER} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[#FDFBF7] truncate">{t.title}</div>
                    <div className="text-[#A1A4AB] text-sm truncate">{t.artist}</div>
                  </div>
                  <button onClick={() => playQueue(openDetail.tracks, i, "normal")} className="btn-ghost text-xs" data-testid={`playlist-track-play-${t.id}`}>
                    <Play className="w-4 h-4 inline" strokeWidth={1.5} />
                  </button>
                  <button onClick={() => removeTrack(t.id)} className="btn-ghost text-xs text-red-300" data-testid={`playlist-track-remove-${t.id}`}>
                    <Trash2 className="w-4 h-4 inline" strokeWidth={1.5} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
